import type {
  DefaultAttributes,
  DepositFormattedLimits,
  DepositLineupSelfLimits,
  FormattedLimitItem,
  FormattedLimits,
  FormattedSelfLimits,
  LineupFormattedLimits,
  MemberResponsibleGamingColumns,
  PeriodType
} from '@library/types/memberResponsibleGaming';
import type {
  LimitType,
  MemberIncluded,
  MemberIncludedItem
} from '@library/types/members';
import {
  getDateTime,
  getFormattedDateTime
} from '@bo-utils/members/memberCommon';
import type { DateTime } from 'luxon';
import type { TableColumn } from '@library/types/utils';
import { getSelfLimits } from '@bo-utils/members/memberResponsibleGamingSelfLimits';

const getEndDateTime = ({
  fromDateTime
}: {
  fromDateTime: DateTime | undefined;
}): string => {
  if (!fromDateTime) {
    return '';
  }

  const endDateTime = fromDateTime?.plus({ days: 90 });

  const { formattedDate: endDate, formattedTime: endTime } =
    getFormattedDateTime({
      dateTime: endDateTime
    });

  return `${endDate} at ${endTime}`;
};

const calcLimitProgress = ({
  limitValue,
  progressValue
}: {
  limitValue: number | null | undefined;
  progressValue: number | null | undefined;
}): { remainingValue: number; progressPercent: number } => {
  const limit = limitValue || 0;
  const progress = progressValue || 0;

  return {
    remainingValue: limit ? limit - progress : 0,
    progressPercent: limit ? (progress * 100) / limit : 0
  };
};
const getFormattedLimit = ({
  limitValue,
  progressValue,
  defaultAttributes
}: {
  limitValue: number | null | undefined;
  progressValue: number | null | undefined;
  defaultAttributes: DefaultAttributes;
}): FormattedLimitItem => {
  return {
    ...calcLimitProgress({
      limitValue: limitValue || 0,
      progressValue: progressValue || 0
    }),
    ...defaultAttributes,
    limitValue: limitValue || 0,
    progressValue: progressValue || 0
  };
};

const getFormattedLimits = ({
  limitType,
  memberIncluded
}: {
  limitType: LimitType;
  memberIncluded: MemberIncluded;
}): FormattedLimits => {
  const memberlimits: MemberIncludedItem | undefined = memberIncluded.find(
    (includedType) =>
      includedType.type === 'limit' &&
      includedType.attributes.limit_type === limitType
  );

  const isDepositLimitType = limitType === 'deposit';

  if (!memberlimits) {
    return isDepositLimitType
      ? { depositLimitAmounts: [], depositLimitRestrictionEndDate: '' }
      : {
          lineupLimitCounts: [],
          lineupLimitAmounts: [],
          lineupLimitRestrictionEndDate: ''
        };
  }

  const {
    attributes: {
      created_at: createdAt,
      daily_limit_cents: dailyLimitCents,
      daily_limit_entries_progress: dailyLimitEntriesProgress,
      daily_limit_entries: dailyLimitEntries,
      daily_limit_progress: dailyLimitProgress,
      monthly_limit_entries_progress: monthlyLimitEntriesProgress,
      thirty_day_limit_cents: monthlyLimitCents,
      thirty_day_limit_entries: monthlyLimitEntries,
      thirty_day_limit_progress: monthlyLimitProgress,
      updated_at: updatedAtDateString,
      weekly_limit_cents: weeklyLimitCents,
      weekly_limit_entries_progress: weeklyLimitEntriesProgress,
      weekly_limit_entries: weeklyLimitEntries,
      weekly_limit_progress: weeklyLimitProgress
    }
  } = memberlimits;

  const updatedDateTime = getDateTime({ dateString: updatedAtDateString });

  const { formattedDate: updatedDate, formattedTime: updatedTime } =
    getFormattedDateTime({ dateTime: updatedDateTime });

  const defaultAttributes = {
    createdAt,
    limitType,
    updatedDate,
    updatedTime
  };

  const getDefaultAttributes = (periodType: PeriodType) => ({
    ...defaultAttributes,
    periodType
  });

  const defaultDailyAttributes = getDefaultAttributes('Daily');
  const defaultWeeklyAttributes = getDefaultAttributes('Weekly');
  const defaultMonthlyAttributes = getDefaultAttributes('Monthly');

  const dailyLimitAmount = getFormattedLimit({
    limitValue: dailyLimitCents,
    progressValue: dailyLimitProgress,
    defaultAttributes: defaultDailyAttributes
  });

  const weeklyLimitAmount = getFormattedLimit({
    limitValue: weeklyLimitCents,
    progressValue: weeklyLimitProgress,
    defaultAttributes: defaultWeeklyAttributes
  });

  const monthlyLimitAmount = getFormattedLimit({
    limitValue: monthlyLimitCents,
    progressValue: monthlyLimitProgress,
    defaultAttributes: defaultMonthlyAttributes
  });

  const limitRestrictionEndDate = getEndDateTime({
    fromDateTime: updatedDateTime
  });

  if (isDepositLimitType) {
    return {
      depositLimitAmounts: [
        dailyLimitAmount,
        weeklyLimitAmount,
        monthlyLimitAmount
      ],
      depositLimitRestrictionEndDate: limitRestrictionEndDate
    };
  }

  const dailyLimitCount = getFormattedLimit({
    limitValue: dailyLimitEntries,
    progressValue: dailyLimitEntriesProgress,
    defaultAttributes: defaultDailyAttributes
  });

  const weeklyLimitCount = getFormattedLimit({
    limitValue: weeklyLimitEntries,
    progressValue: weeklyLimitEntriesProgress,
    defaultAttributes: defaultWeeklyAttributes
  });

  const monthlyLimitCount = getFormattedLimit({
    limitValue: monthlyLimitEntries,
    progressValue: monthlyLimitEntriesProgress,
    defaultAttributes: defaultMonthlyAttributes
  });

  return {
    lineupLimitCounts: [dailyLimitCount, weeklyLimitCount, monthlyLimitCount],
    lineupLimitAmounts: [
      dailyLimitAmount,
      weeklyLimitAmount,
      monthlyLimitAmount
    ],
    lineupLimitRestrictionEndDate: limitRestrictionEndDate
  };
};

export const getLimits = ({
  memberIncluded
}: {
  memberIncluded: MemberIncluded;
}): DepositLineupSelfLimits => {
  const deposit: DepositFormattedLimits = getFormattedLimits({
    limitType: 'deposit',
    memberIncluded
  }) as DepositFormattedLimits;

  const lineups: LineupFormattedLimits = getFormattedLimits({
    limitType: 'entry',
    memberIncluded
  }) as LineupFormattedLimits;

  const selfExclusions: FormattedSelfLimits = getSelfLimits({
    memberIncluded,
    selfLimitType: 'self_exclusion'
  });

  const selfTimeouts: FormattedSelfLimits = getSelfLimits({
    memberIncluded,
    selfLimitType: 'self_timeout'
  });

  return {
    ...deposit,
    ...lineups,
    selfLimits: [...selfTimeouts, ...selfExclusions]
  };
};

export const memberRGTableHeaders: Array<
  TableColumn<MemberResponsibleGamingColumns>
> = [
  {
    column: 'period',
    essential: true,
    label: 'Period',
    sortEnabled: false,
    width: 'w-1/5'
  },
  {
    column: 'value',
    essential: true,
    label: 'Limit',
    sortEnabled: false,
    width: 'w-1/5'
  },
  {
    column: 'progress',
    essential: true,
    label: 'Progress',
    sortEnabled: false,
    width: 'w-1/5'
  },
  {
    column: 'remaining',
    essential: true,
    label: 'Remaining',
    sortEnabled: false,
    width: 'w-1/5'
  },
  {
    column: 'updatedAt',
    essential: true,
    label: 'Updated At',
    sortEnabled: false,
    width: 'w-1/5'
  }
];
